import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ArrowLeft } from 'lucide-react';

const BREAK_REASONS = ['Vacation', 'Medical', 'Family', 'School Break', 'Financial', 'Other'];

const PauseForBreak = ({ onBack }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [filter, setFilter] = useState('active');
  const [breakStartDate, setBreakStartDate] = useState('');
  const [breakEndDate, setBreakEndDate] = useState('');
  const [breakReason, setBreakReason] = useState('Vacation');
  const [processing, setProcessing] = useState(false);
  const [reactivationLog, setReactivationLog] = useState([]);
  const [overdueReturns, setOverdueReturns] = useState([]);

  useEffect(() => {
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString().split('T')[0];

  const loadStudents = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(query(collection(db, 'students')));
      const allStudents = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Auto-reactivate students whose return date has passed
      const toReactivate = allStudents.filter(s =>
        s.onBreak === true &&
        s.breakEndDate &&
        s.breakEndDate < todayISO &&
        !s.archived
      );

      if (toReactivate.length > 0) {
        const log = [];
        await Promise.all(toReactivate.map(async s => {
          await updateDoc(doc(db, 'students', s.id), {
            onBreak: false,
            breakStartDate: null,
            breakEndDate: null,
            breakReason: null,
            reactivatedAt: new Date().toISOString(),
          });
          log.push(`${s.firstName} ${s.lastName} (return date: ${s.breakEndDate})`);
        }));
        setReactivationLog(log);
        // Re-fetch after reactivation
        const refreshed = await getDocs(query(collection(db, 'students')));
        processStudents(refreshed.docs.map(d => ({ id: d.id, ...d.data() })));
      } else {
        processStudents(allStudents);
      }
    } catch (error) {
      console.error('Error loading students:', error);
      alert('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const processStudents = (allStudents) => {
    const afterSchool = allStudents.filter(s => {
      if (s.archived === true || s.programStatus === 'inactive') return false;
      if (!s.programSelection) return false;
      const prog = (s.programSelection || '').toLowerCase();
      if (prog.includes('camp') && !prog.includes('after')) return false;
      if (prog.includes('saturday')) return false;
      return prog.includes('after school') || prog.includes('pickup') || s.needsPickup === true;
    });

    // Find students still on break but whose date is today (edge case — show as overdue)
    const overdue = afterSchool.filter(s =>
      s.onBreak === true && s.breakEndDate && s.breakEndDate <= todayISO
    );
    setOverdueReturns(overdue);

    const filtered = afterSchool.filter(s => {
      if (filter === 'active') return !s.onBreak;
      if (filter === 'on-break') return s.onBreak === true;
      return true;
    });

    filtered.sort((a, b) => {
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
    });

    setStudents(filtered);
  };

  const toggleStudent = (id) =>
    setSelectedStudents(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelectedStudents(selectedStudents.length === students.length ? [] : students.map(s => s.id));

  const handlePauseStudents = async () => {
    if (!selectedStudents.length) { alert('Please select at least one student'); return; }
    if (!breakEndDate) { alert('Please select a return date'); return; }
    if (breakStartDate && breakEndDate && breakStartDate > breakEndDate) { alert('Start date must be before return date'); return; }
    const startLabel = breakStartDate ? new Date(breakStartDate + 'T12:00:00').toLocaleDateString() : 'today';
    if (!window.confirm(`Pause ${selectedStudents.length} student(s) from ${startLabel} until ${new Date(breakEndDate + 'T12:00:00').toLocaleDateString()}?\n\nThey will not appear in daily rosters during this period.`)) return;

    setProcessing(true);
    try {
      await Promise.all(selectedStudents.map(id =>
        updateDoc(doc(db, 'students', id), {
          onBreak: true,
          breakStartDate: breakStartDate || new Date().toISOString().split('T')[0],
          breakEndDate,
          breakReason,
        })
      ));
      alert(`✅ Paused ${selectedStudents.length} student(s) until ${new Date(breakEndDate + 'T12:00:00').toLocaleDateString()}`);
      setSelectedStudents([]);
      setBreakStartDate('');
      setBreakEndDate('');
      loadStudents();
    } catch (err) {
      alert('Failed to pause students. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateStudents = async (ids = selectedStudents) => {
    if (!ids.length) { alert('Please select at least one student'); return; }
    if (!window.confirm(`Reactivate ${ids.length} student(s)? They will appear in daily rosters again.`)) return;

    setProcessing(true);
    try {
      await Promise.all(ids.map(id =>
        updateDoc(doc(db, 'students', id), {
          onBreak: false,
          breakStartDate: null,
          breakEndDate: null,
          breakReason: null,
          reactivatedAt: new Date().toISOString(),
        })
      ));
      alert(`✅ Reactivated ${ids.length} student(s)`);
      setSelectedStudents([]);
      loadStudents();
    } catch (err) {
      alert('Failed to reactivate students. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const inputStyle = { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' };
  const btnStyle = (color, disabled) => ({
    padding: '0.5rem 1.25rem', backgroundColor: disabled ? '#9ca3af' : color,
    color: 'white', border: 'none', borderRadius: '6px',
    fontSize: '0.875rem', fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer',
  });

  return (
    <div style={{ padding: '1.5rem', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>⏸️ Pause Students for Break</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Temporarily remove students from daily rosters during breaks</p>
      </div>

      {/* Auto-reactivation log */}
      {reactivationLog.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
          <div style={{ fontWeight: '700', color: '#15803d', fontSize: '0.875rem', marginBottom: '0.35rem' }}>
            ✅ Auto-reactivated {reactivationLog.length} student(s) — return date has passed
          </div>
          {reactivationLog.map((entry, i) => (
            <div key={i} style={{ fontSize: '0.8rem', color: '#166534' }}>· {entry}</div>
          ))}
        </div>
      )}

      {/* Overdue returns alert */}
      {overdueReturns.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px' }}>
          <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            ⚠️ {overdueReturns.length} student(s) overdue for return — please reactivate
          </div>
          {overdueReturns.map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#991b1b', marginBottom: '0.25rem' }}>
              <span>{s.firstName} {s.lastName} — return was {s.breakEndDate}</span>
              <button onClick={() => handleReactivateStudents([s.id])} style={btnStyle('#10b981', processing)}>
                Reactivate
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[['active', 'Active', '#10b981'], ['on-break', 'On Break', '#f59e0b']].map(([val, label, color]) => (
              <button key={val} onClick={() => { setFilter(val); setSelectedStudents([]); }} style={{ padding: '0.5rem 1rem', backgroundColor: filter === val ? color : '#e5e7eb', color: filter === val ? 'white' : '#374151', border: 'none', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>

          {/* Pause controls */}
          {filter === 'active' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <select value={breakReason} onChange={e => setBreakReason(e.target.value)} style={inputStyle}>
                {BREAK_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Start Date:</label>
                <input type="date" value={breakStartDate} onChange={e => setBreakStartDate(e.target.value)}
                  min={(() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; })()}
                  style={{ ...inputStyle, width: '140px' }} />
                <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>blank = today</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Return Date:</label>
                <input type="date" value={breakEndDate} onChange={e => setBreakEndDate(e.target.value)}
                  min={breakStartDate || (() => { const t = new Date(); t.setDate(t.getDate()+1); return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; })()}
                  style={inputStyle} />
              </div>
              <button onClick={handlePauseStudents} disabled={!selectedStudents.length || !breakEndDate || processing} style={btnStyle('#f59e0b', !selectedStudents.length || !breakEndDate || processing)}>
                {processing ? 'Processing...' : `⏸️ Pause ${selectedStudents.length} Student(s)`}
              </button>
            </div>
          )}

          {filter === 'on-break' && (
            <button onClick={() => handleReactivateStudents()} disabled={!selectedStudents.length || processing} style={btnStyle('#10b981', !selectedStudents.length || processing)}>
              {processing ? 'Processing...' : `▶️ Reactivate ${selectedStudents.length} Student(s)`}
            </button>
          )}
        </div>
      </div>

      {/* Student list */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: '700', margin: 0 }}>
            {filter === 'active' ? 'Active Students' : 'Students on Break'} ({students.length})
          </h2>
          <button onClick={toggleAll} style={{ padding: '0.4rem 0.85rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}>
            {selectedStudents.length === students.length && students.length > 0 ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>Loading...</p>
        ) : students.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>No {filter === 'active' ? 'active' : 'paused'} students found</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {students.map(student => (
              <div key={student.id} onClick={() => toggleStudent(student.id)} style={{ padding: '0.85rem 1rem', border: '2px solid', borderColor: selectedStudents.includes(student.id) ? '#3b82f6' : '#e5e7eb', backgroundColor: selectedStudents.includes(student.id) ? '#eff6ff' : 'white', borderRadius: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                  <input type="checkbox" checked={selectedStudents.includes(student.id)} onChange={() => {}} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1f2937' }}>{student.firstName} {student.lastName}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.1rem' }}>
                      Grade {student.grade} · {student.programSelection}
                      {student.onBreak && student.breakEndDate && (
                        <span style={{ marginLeft: '0.5rem', color: '#f59e0b', fontWeight: '600' }}>
                          · Returns: {new Date(student.breakEndDate + 'T12:00:00').toLocaleDateString()}
                        </span>
                      )}
                      {student.onBreak && student.breakReason && (
                        <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>({student.breakReason})</span>
                      )}
                    </div>
                  </div>
                </div>
                {student.onBreak && (
                  <span style={{ padding: '0.2rem 0.65rem', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '12px', fontSize: '0.72rem', fontWeight: '600', flexShrink: 0 }}>
                    ⏸️ On Break
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '0.82rem', color: '#1e40af', lineHeight: 1.7 }}>
        <strong>💡 How This Works</strong>
        <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
          <li>Paused students won't appear in Today's Roster, Front Desk, or Pickup</li>
          <li>Payment Tracking shows paused students separately — not as overdue</li>
          <li>Students auto-reactivate when their return date passes</li>
          <li>You can manually reactivate anytime</li>
        </ul>
      </div>
    </div>
  );
};

export default PauseForBreak;